//@ts-nocheck

'use client'

import React, { use, useEffect, useState } from "react";

import * as S from './HeroProfile.styles'

import { Heading, Paragraph } from '../../../atoms/Typography/Typography.component'
import { Container, Row, Col, SpacerBase } from '../../../atoms/GridSystem'
import Button from "../../../atoms/Button/Button.component";
import Icon from "../../../atoms/Icon/Icon.component";
import UploadForm from "../../../molecules/UploadForm/UploadForm.component";
import { useLoading } from "../../../../contexts/Loading.context";
import Spinner from "../../../atoms/Spinner/Spinner.component";
import { useUserMeta } from "../../../../contexts/UserMeta.context";
import { getPageLocale } from "@caffeina-siad/utils";
import PhotoList from '../../PhotoList/PhotoList.component'
import ItemsGrouped from "../../../molecules/ItemsGrouped/ItemsGrouped.component";
import Tag from "../../../atoms/Tag/Tag.component";

import Image from 'next/image'

import SectionThemeProvider from '../../../../contexts/SectionTheme.context'

import { format, isValid } from 'date-fns'
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

// Local preview only. Production builds have NODE_ENV=production, so this stays off even if the env var is set.
const isDevUploadPreview =
  process.env.NODE_ENV === 'development' &&
  process.env.NEXT_PUBLIC_DEV_PHOTO_UPLOAD_PREVIEW === 'true'

const HeroProfile = ({ accessToken, contestId, fileUpload, getUserUploadedPhotos, authorize, contestData }) => {
  const [ isUploading, setIsUploading ] = useState(false)
  const [ showBanner, setShowBanner ] = useState(true)
  const [ validSession, setValidSession ] = useState(false)
  const [ accToken, setAccToken ] = useState(accessToken)
  const [ showPhotoList, setShowPhotoList ] = useState(false)
  const [ region, setRegion ] = useState('upload')
  const [ contestStatus, setContestStatus ] = useState(null)
  const [ authStatus, setAuthStatus ] = useState<'pending' | 'signedOut'>('pending')

  const router = useRouter()
  const t = useTranslations()

  const { handleSetUserMeta, handleGetUserMeta, webAuthClient, deleteUserMeta } = useUserMeta()
  const userMeta = handleGetUserMeta()

  const handleShowBanner = () => setShowBanner(!showBanner)

  const redirectToLogin = () => {
    const authUrl = process.env.NEXT_PUBLIC_SITE_URL_AUTH
    if (!authUrl) {
      setAuthStatus('signedOut')
      return
    }
    router.push(`${authUrl}/${getPageLocale()}`)
  }

  const handleAuthorize = (accessToken) => {
    authorize(accessToken).then((res) => {

      if (!res) {
        redirectToLogin()
        return
      }

      const user = {
        name: res.auth0.user_metadata.name,
        surname: res.auth0.user_metadata.surname,
        companyName: res.auth0.user_metadata.companyName,
        registered: res.data.createdAt,
        accessToken
      }
      
      handleSetUserMeta(user)
      setValidSession(true)
    })
  }

  const getAccessTokenFromStorage = () => {
    const userMeta = handleGetUserMeta()

    return userMeta?.accessToken
  }

  const renderRegion = (region) => {
    switch (region) {
      case 'upload':
        return uploadRegion()
      case 'photoList':
        return photoListRegion()
      default:
        return uploadRegion()
    }
  }

  const uploadRegion = () => {
    return (
      <S.HeroProfileUploadWrapper className="hero-profile-upload-wrapper">
        <S.HeroProfileUploadFormWrapper show={!showBanner} className="hero-profile-upload-form-wrapper">
          <UploadForm fileUpload={fileUpload} contestId={contestId} accessToken={accToken} />
        </S.HeroProfileUploadFormWrapper>
        <S.HeroProfileImageText show={showBanner} className="hero-profile-image-text">
          <S.HeroProfileImageWrapper className="hero-profile-image-wrapper">
            {/* <Image src={'/images/register-background.jpg'} alt={'Profilo utente'} fill/> */}
          </S.HeroProfileImageWrapper>
          <Heading
            content={t('privateArea.title')}
            weight={"medium"}
          />
          <SpacerBase xs={3} />
          <Paragraph
            content={t('privateArea.description')}
            weight={"medium"}
          />
          <SpacerBase xs={3} />
          <S.HeroProfileImageTextButtonWrapper>
            <Button 
              label={t('privateArea.ctaLabel')}
              variant={'secondary'}
              scrollTo={'info'}
              iconEnd={'arrow-down'}
            />
            <Button 
              label={t('privateArea.uploadPhotoCta')}
              variant={'primary'}
              inheritedColorScheme={'darkGreen'}
              handleClick={handleShowBanner}
              iconEnd={'arrow-right'}
              iconEndSize={'regular'}
            />
          </S.HeroProfileImageTextButtonWrapper>
        </S.HeroProfileImageText>

        <S.HeroProfileIconWrapper position={'top-right'} className="hero-profile-icon-wrapper">
          <Icon name={'circle'} useIconColor/>
        </S.HeroProfileIconWrapper>
        <S.HeroProfileIconWrapper position={'bottom-left'} className="hero-profile-icon-wrapper">
          <Icon name={'leaf-green-big'} useIconColor unlockedProportions/>
        </S.HeroProfileIconWrapper>
      </S.HeroProfileUploadWrapper>
    )
  }

  const photoListRegion = () => {
    return (
      <PhotoList accessToken={accToken} contestId={contestId} getUserUploadedPhotos={getUserUploadedPhotos} />
    )
  }

  const handleLogout = (e: React.FormEvent) => {
      if (!webAuthClient) return;
  
      e.preventDefault();
      handleSetUserMeta({})
      webAuthClient.logout({
        returnTo: process.env.NEXT_PUBLIC_SITE_URL
      });
  }
  
  const isContestExpired = (contestData) => {
    const { endAt } = contestData
    return new Date().getTime() > new Date(endAt).getTime()
  }

  useEffect(() => {
    if (isDevUploadPreview) {
      handleSetUserMeta({
        name: 'Local',
        surname: 'Preview',
        companyName: 'SIAD',
        registered: new Date().toISOString(),
        accessToken: 'local-preview',
      })
      setAccToken('local-preview')
      setValidSession(true)
      return
    }

    const token = accessToken || getAccessTokenFromStorage()
    if (!token) {
      redirectToLogin()
      return
    }

    setAccToken(token)
    router.replace(window.location.pathname)
    handleAuthorize(token)

  }, [accessToken])

  useEffect(() => {
    if (isDevUploadPreview) {
      setContestStatus('valid')
      return
    }
    if (!contestData) {
      setContestStatus('no_contest')
      return
    }
    if (isContestExpired(contestData)) setContestStatus('expired')
    else setContestStatus('valid')
  }, [contestData])

  if (authStatus === 'signedOut') {
    return (
      <Paragraph
        content="Accesso non configurato in locale. Riavvia il dev server dopo aver impostato NEXT_PUBLIC_DEV_PHOTO_UPLOAD_PREVIEW=true."
        typo="paragraphMD"
      />
    )
  }

  return validSession && userMeta && Object.keys(userMeta).length > 0 ? (
    <SectionThemeProvider
      colorScheme={'darkGreen'}
      id={'hero'}
    >
      <S.HeroProfileWrapper className="hero-profile-wrapper">
        <Container>
          <Row>
            <Col xs={12} lg={4}>
              <S.HeroProfileUserData className="hero-profile-user-data">
                <Heading 
                  content={t('privateArea.hello')}
                  as={"div"}
                  typo={"headingXXL"}
                  weight={"medium"}
                />
                <Heading 
                  content={`<span class="highlighted">${userMeta.name} ${userMeta.surname}</span>`}
                  as={"div"}
                  typo={"headingXXL"}
                  weight={"medium"}
                />
                <SpacerBase xs={3} />
                <Tag items={[{
                  colorScheme: 'darkBlue',
                  text: userMeta.companyName
                }]}/>
                <SpacerBase xs={4} xl={6} />
                {/* <Paragraph 
                  content={`Iscritto dal ${format(userMeta.registered, 'dd-MM-yyyy')}`}
                  as={"p"}
                  typo={"paragraphSM"}
                  weight={"regular"}
                />
                <SpacerBase xs={4} xl={6} /> */}
                <Button 
                  label={t('global.logout')}
                  variant={'tertiary'}
                  handleClick={handleLogout}
                />
              </S.HeroProfileUserData>
            </Col>
            <Col xs={12} lg={8}>
              { contestStatus === 'valid' ? (
                <div>
                  <Button 
                    label={region === 'upload' ? t('privateArea.submissionsListCta') : t('privateArea.uploadPhotoCta')}
                    variant={'primary'}
                    handleClick={() => {
                      region === 'upload' ? setRegion('photoList') : setRegion('upload')
                    }}
                  />
                  <SpacerBase xs={2} xl={6} />
                </div>
              ) : null }
              { contestStatus === 'valid' ? renderRegion(region) : (
                <S.HeroProfileInvalidContestWrapper>
                  <Heading
                    content={contestStatus === 'no_contest' ? t('privateArea.noContest') : t('privateArea.contestExpired')}
                    as={"h3"}
                    typo={"headingXL"}
                    weight={"medium"}
                  />
                </S.HeroProfileInvalidContestWrapper>
              )}
            </Col>
          </Row>
        </Container>
      </S.HeroProfileWrapper>
    </SectionThemeProvider>
  ) : <Spinner />;
}

export default HeroProfile